/**
 * WelcomeDialog — se muestra en el PRIMER LOGIN de un usuario
 * y ofrece activar el Modo Aprendiz 7 días.
 *
 * Heurística de "primer login":
 *  - Hay usuario autenticado
 *  - NO existe localStorage[`dnf_welcome_seen_${user.id}`]
 *  - NO tiene `learner_mode_until` activo todavía
 *
 * Al cerrar (sea aceptando o saltando) se marca el flag en localStorage
 * para no volver a mostrarse en ese navegador.
 */
import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLearnerMode } from "@/components/LearnerHint";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { GraduationCap, Rocket, BookOpen } from "lucide-react";
import { toast } from "sonner";

const LS_PREFIX = "dnf_welcome_seen_";

export default function WelcomeDialog() {
  const { user } = useAuth();
  const learner = useLearnerMode();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user || user === false) return;
    const key = LS_PREFIX + user.id;
    const seen = localStorage.getItem(key);
    if (seen) return;
    if (learner.active) {
      // Ya tiene aprendiz activo → solo marcar como visto
      localStorage.setItem(key, "1");
      return;
    }
    // Mostrar dialog tras 600ms para que termine la animación de login
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
  }, [user, learner.active]);

  const close = (markSeen = true) => {
    if (user && user !== false && markSeen) {
      localStorage.setItem(LS_PREFIX + user.id, "1");
    }
    setOpen(false);
  };

  const handleActivar = async () => {
    try {
      await learner.activate();
      toast.success("Modo aprendiz activado 7 días 🎓 — busca el punto amber en los botones");
    } catch {
      toast.error("No se pudo activar. Inténtalo desde Perfil.");
    }
    close(true);
  };

  if (!user || user === false) return null;

  const nombre = user.name?.split(" ")[0] || "";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(true); }}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="welcome-dialog"
      >
        <DialogHeader>
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 shadow-lg ring-4 ring-amber-100">
            <Rocket className="w-8 h-8 text-zinc-900" />
          </div>
          <DialogTitle className="text-center text-xl">
            ¡Bienvenido{nombre ? `, ${nombre}` : ""}!
          </DialogTitle>
          <DialogDescription className="text-center pt-1">
            Es tu primera vez por aquí. ¿Quieres que te guíe la app durante una semana?
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 my-3">
          <div className="flex items-start gap-3">
            <GraduationCap className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-zinc-700">
              <div className="font-semibold text-amber-800 mb-1">Modo Aprendiz (7 días)</div>
              <ul className="space-y-1 text-xs">
                <li>• Punto amber pulsante en los botones más importantes</li>
                <li>• Aprendes haciendo, sin tutoriales largos</li>
                <li>• Se desactiva solo a los 7 días (o cuando quieras desde Perfil)</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 mb-2">
          <div className="flex items-start gap-3">
            <BookOpen className="w-5 h-5 text-zinc-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-zinc-700">
              <div className="font-semibold text-zinc-800 mb-1">Manual a un click</div>
              <p className="text-xs">
                En cualquier pantalla tienes abajo a la izquierda un botón <b>Ayuda · &lt;módulo actual&gt;</b> que abre el manual en la sección correspondiente.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          <Button
            variant="outline"
            onClick={() => close(true)}
            data-testid="welcome-skip-btn"
            className="flex-1 border-zinc-300"
          >
            Ya conozco la app
          </Button>
          <Button
            onClick={handleActivar}
            data-testid="welcome-activate-btn"
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-zinc-900 font-semibold"
          >
            <GraduationCap className="w-4 h-4 mr-2" />
            Activar 7 días
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
